import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendCustomMetric} from '@github-ui/stats'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'

type CACHE_RESULT = 'hit' | 'hit-hot' | 'miss' | 'unknown'

// Query metadata for Issue Show (from IssueViewerViewQuery.graphql.ts)
const ISSUE_SHOW_QUERY_NAME = 'IssueViewerViewQuery'
// Issue Show route pattern: /:owner/:repo/issues/:number
const ISSUE_SHOW_PATTERN = /^https?:\/\/[^/]+\/([^/]+)\/([^/]+)\/issues\/(\d+)$/
const HPC_EVENT_NAME = 'web-vitals:hpc'

const MAX_EVENT_AGE_MS = 500

// Module-level singleton state for HPC event handling
export const hpcState: {
  lastResult: CACHE_RESULT | null
  pendingEvent: CustomEvent | null
  pendingEventTime: number | null
  listenerRegistered: boolean
  sendAnalyticsEvent: SendAnalyticsEventFunction | null
  enabled: boolean
  resourceMetricsEnabled: boolean
} = {
  lastResult: null,
  pendingEvent: null,
  pendingEventTime: null,
  listenerRegistered: false,
  sendAnalyticsEvent: null,
  enabled: false,
  resourceMetricsEnabled: false,
}

const resetHPCState = () => {
  hpcState.lastResult = null
  hpcState.pendingEvent = null
  hpcState.pendingEventTime = null
}

type ResourceType = 'css' | 'js'

type ResourceStats = {
  type: ResourceType
  count: number
  maxDuration: number
  totalDuration: number
  biggestResourceName: string | null
}

type ResourceConfig = {
  type: ResourceType
  initiatorType: string
  extension: string
}

const RESOURCE_CONFIGS: readonly ResourceConfig[] = [
  {type: 'css', initiatorType: 'link', extension: '.css'},
  {type: 'js', initiatorType: 'script', extension: '.js'},
] as const

const computeResourceStats = (): readonly ResourceStats[] => {
  const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[]

  return RESOURCE_CONFIGS.map(config => {
    const stats: ResourceStats = {
      type: config.type,
      count: 0,
      maxDuration: 0,
      totalDuration: 0,
      biggestResourceName: null,
    }

    let minStart = 0
    let maxEnd = 0
    let biggest: PerformanceResourceTiming | null = null

    for (const entry of resourceEntries) {
      if (entry.initiatorType === config.initiatorType && entry.name.endsWith(config.extension)) {
        const duration = entry.responseEnd - entry.requestStart
        stats.count++
        stats.maxDuration = Math.max(stats.maxDuration, duration)
        minStart = minStart === 0 ? entry.requestStart : Math.min(minStart, entry.requestStart)
        maxEnd = Math.max(maxEnd, entry.responseEnd)
        if (!biggest || duration > biggest.responseEnd - biggest.requestStart) {
          biggest = entry
        }
      }
    }

    stats.totalDuration = maxEnd - minStart
    if (biggest) {
      stats.biggestResourceName = biggest.name
    }

    return stats
  })
}

const getServerTiming = (): readonly PerformanceServerTiming[] => {
  const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
  if (navigationEntries.length > 0) {
    return navigationEntries[0]?.serverTiming || []
  }
  return []
}

export const initHPCData = (sendAnalyticsEvent: SendAnalyticsEventFunction) => {
  if (hpcState.enabled) return

  const isCustomHPCMetricEnabled = isFeatureEnabled('issues_react_custom_hpc_metric')
  const isResourceMetricsEnabled = isFeatureEnabled('issues_react_resource_metrics')

  if (isCustomHPCMetricEnabled) {
    hpcState.sendAnalyticsEvent = sendAnalyticsEvent
    // Register the HPC event listener (singleton - only registers once)
    ensureHPCListenerRegistered()
    hpcState.enabled = true
    hpcState.resourceMetricsEnabled = isResourceMetricsEnabled
  }
}

export const reportHPCData = (event: CustomEvent, result: CACHE_RESULT | null) => {
  if (result === null) return
  sendCustomMetric(
    {
      name: 'ISSUES_SHOW_HPC',
      value: event.detail.hpc,
      tags: {
        cpu: event.detail.cpu || 'unknown',
        cache_result: result,
        mechanism: event.detail.mechanism || 'unknown',
      },
    },
    undefined,
    1,
  )

  if (hpcState.sendAnalyticsEvent) {
    const serverTiming = getServerTiming().map(timing => ({
      name: timing.name,
      duration: timing.duration,
    }))
    const resourcesStats = hpcState.resourceMetricsEnabled ? computeResourceStats() : []
    hpcState.sendAnalyticsEvent('issues.hpc', 'issue#show', {
      hpc: event.detail.hpc,
      cpu: event.detail.cpu || 'unknown',
      cache_result: result,
      mechanism: event.detail.mechanism || 'unknown',
      server_timing: JSON.stringify(serverTiming),
      resource_stats: JSON.stringify(resourcesStats),
    })
  }
}

const hpcEventListener = ((event: CustomEvent) => {
  if (!ISSUE_SHOW_PATTERN.test(event.detail.name)) return
  if (hpcState.lastResult !== null) {
    const result = hpcState.lastResult
    resetHPCState()
    reportHPCData(event, result)
  } else {
    hpcState.pendingEvent = event
    hpcState.pendingEventTime = Date.now()
  }
}) as EventListener

export const tryReportPendingHPCEvent = (queryName: string, queryResult: CACHE_RESULT | null) => {
  if (!hpcState.enabled) return
  if (queryName !== ISSUE_SHOW_QUERY_NAME || queryResult === null) return
  hpcState.lastResult = queryResult
  if (hpcState.pendingEvent) {
    const result = hpcState.lastResult
    const event = hpcState.pendingEvent
    const timeDelta = Date.now() - (hpcState.pendingEventTime || 0)
    resetHPCState()
    // If the time between the HPC event and the query result is too long, discard it since it might not match
    if (timeDelta <= MAX_EVENT_AGE_MS) {
      reportHPCData(event, result)
    }
  }
}

export const ensureHPCListenerRegistered = () => {
  if (!hpcState.listenerRegistered) {
    document.addEventListener(HPC_EVENT_NAME, hpcEventListener)
    hpcState.listenerRegistered = true
  }
}
